/* eslint-disable */
import React, { FC, useEffect, useState } from 'react';
import Rooms from '../rooms';
import Spinner from '../../spinner';
import { useParams, usePathname, useRouter } from 'next/navigation';
import Link from 'next/link';
import { PROJECT_PRODUCTS_UPDATED } from '@/helpers/constants/events';
import { eventEmitter } from './EventEmitter';
import Typography from '@/components/atoms/project-builder/typography';
import Popup from '@/components/atoms/project-builder/popup';
import useProjectBuilder from '@/lib/hooks/useProjectBuilder';
import useAccount from '@/lib/hooks/useAccount';
import Button from '@/components/atoms/project-builder/button';
import AccordionButton from '@/components/atoms/project-builder/accordion';
import Loader from '../../loader';
import { mutate } from 'swr';
interface IProps {
  open: boolean;
  onCancel: any;
  productToAdd: any;
  projectForm?: any;
  fromProjectBuilder?: boolean;
}
const AddProductToProject: FC<IProps> = ({ open, onCancel, productToAdd, projectForm, fromProjectBuilder = false }) => {
  const { account } = useAccount();
  const { locale } = useParams();
  const pathname = usePathname();
  const router = useRouter();
  const isAddProductsPage =
    !!pathname && (pathname.toLowerCase().includes('project-builder')); 
  const effectiveFromProjectBuilder =  isAddProductsPage;
  const [selectedProjects, setSelectedProjects] = useState<any>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [loadingRooms, setLoadingRooms] = useState<boolean>(projectForm?.shoppingListId ? true : false);

  const [success, setSuccess] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const { getProjectById, shoppingLists, updateShoppingList, updateProjectStatus } = useProjectBuilder();
  const [showCreateRoom, setShowCreateRoom] = useState<boolean>(false);
  const [showCreateRoomPDPId, setShowCreateRoomPDPId] = useState<string>('');
  const [newRoomName, setNewRoomName] = useState('');
  const [projectFormList, setProjectFormList] = useState<any>();
  const [visibleCount, setVisibleCount] = useState<number>(3);

  useEffect(() => {
    if (open) {
      setVisibleCount(3);
    }
  }, [open]);

  const cancelRoomPDP = () => {
    setShowCreateRoomPDPId('');
    setNewRoomName('');
    setError('');
  };

  const handleCreateRoomPDP = async (project: any) => {
    const trimmedRoom = newRoomName.trim();
    if (!trimmedRoom) return;

    const checkIsDuplicate = isDuplicate(toTitleCase(trimmedRoom), project?.roomLabels || []);
    if (checkIsDuplicate) {
      setError('Room already exists!');
      setTimeout(() => setError(''), 5000);
      return;
    }
    const roomLables = [...(project?.roomLabels ?? []), toTitleCase(trimmedRoom)];
    const payload = {
      version: project.version,
      shoppingListId: project.shoppingListId,
      items: {
        selectedRoomDetails: roomLables,
      },
    };
    try {
      await updateProjectStatus(payload);
      mutate('/action/projectBuilder/getShoppingLists');
      setNewRoomName('');
      setShowCreateRoomPDPId('');
    } catch (error) {
      console.error('Error on creating room:', error);
    }
  };

  const handleRoomSelection = ({ project, roomLabel, existingItem }: any) => {
    setSelectedProjects((prevSelectedProjects: any) => {
      const projectIndex = prevSelectedProjects.findIndex(
        (selectedProject: any) => selectedProject.shoppingListId === project.shoppingListId,
      );

      if (projectIndex === -1) {
        return [
          ...prevSelectedProjects,
          {
            shoppingListId: project.shoppingListId,
            selectedRooms: [
              {
                roomLabel,
                ...(!!existingItem?.id
                  ? { lineItemId: existingItem?.id, quantity: existingItem?.quantity }
                  : {
                    roomNotes: '',
                    quantity: 1,
                  }),
              },
            ],
          },
        ];
      }

      const selectedProject = prevSelectedProjects[projectIndex];
      const roomIndex = selectedProject.selectedRooms.findIndex((r: any) => r.roomLabel === roomLabel);

      if (roomIndex === -1) {
        // Room not found: Add a new room
        const updatedProject = {
          ...selectedProject,
          selectedRooms: [
            ...selectedProject.selectedRooms,
            {
              roomLabel,
              ...(!!existingItem?.id
                ? { lineItemId: existingItem?.id, quantity: existingItem?.quantity }
                : {
                  roomNotes: '',
                  quantity: 1,
                }),
            },
          ],
        };
        return [
          ...prevSelectedProjects.slice(0, projectIndex),
          updatedProject,
          ...prevSelectedProjects.slice(projectIndex + 1),
        ];
      }

      // Room found: Update if lineItemId is provided or remove otherwise
      const updatedRooms = selectedProject.selectedRooms
        .map((r: any, idx: number) =>
          idx === roomIndex
            ? !!existingItem?.id
              ? {
                ...(r.quantity
                  ? { roomLabel: r.roomLabel, lineItemId: r.lineItemId }
                  : { ...r, quantity: existingItem?.quantity }),
              }
              : null
            : r,
        )
        .filter(Boolean);

      const updatedProject = { ...selectedProject, selectedRooms: updatedRooms };
      return [
        ...prevSelectedProjects.slice(0, projectIndex),
        updatedRooms.length > 0 ? updatedProject : null,
        ...prevSelectedProjects.slice(projectIndex + 1),
      ].filter(Boolean);
    });
  };

  const handleAddNote = ({ shoppingListId, roomLabel, note, existingItem }: any) => {
    setSelectedProjects((prevSelectedProjects: any[]) => {
      const projectIndex = prevSelectedProjects.findIndex((project: any) => project.shoppingListId === shoppingListId);

      if (projectIndex === -1) {
        return [
          ...prevSelectedProjects,
          {
            shoppingListId,
            selectedRooms: [
              {
                roomLabel,
                roomNotes: note,
                ...(existingItem ? { lineItemId: existingItem.id, quantity: existingItem.quantity } : {}),
              },
            ],
          },
        ];
      }

      const updatedProjects = prevSelectedProjects.map((project: any, index: number) => {
        if (index === projectIndex) {
          const roomIndex = project.selectedRooms.findIndex((room: any) => room.roomLabel === roomLabel);

          if (roomIndex === -1) {
            return {
              ...project,
              selectedRooms: [
                ...project.selectedRooms,
                {
                  roomLabel,
                  roomNotes: note,
                  ...(existingItem ? { lineItemId: existingItem.id, quantity: existingItem.quantity } : {}),
                },
              ],
            };
          }

          return {
            ...project,
            selectedRooms: project.selectedRooms.map((room: any, i: number) =>
              i === roomIndex ? { ...room, roomNotes: note } : room,
            ),
          };
        }
        return project;
      });

      return updatedProjects;
    });
  };

  const handleDecrement = (shoppingListId: string, roomLabel: any, existingItem?: any) => {
    setSelectedProjects((prevSelectedProjects: any[]) => {
      const projectIndex = prevSelectedProjects.findIndex((project) => project.shoppingListId === shoppingListId);

      if (projectIndex !== -1) {
        const updatedProjects = prevSelectedProjects.map((project) => {
          if (project.shoppingListId === shoppingListId) {
            const roomIndex = project.selectedRooms.findIndex((r: any) => r.roomLabel === roomLabel);

            if (roomIndex !== -1) {
              const updatedRooms = project.selectedRooms
                .map((r: any) => {
                  if (r.roomLabel === roomLabel) {
                    const currentQuantity = r.quantity || existingItem?.quantity || 1;
                    const updatedQuantity = currentQuantity - 1;

                    // If the quantity goes to 0 and existingItem is provided, add room without quantity
                    if (updatedQuantity === 0 && existingItem) {
                      return { roomLabel, lineItemId: existingItem.id };
                    }

                    // If quantity goes to 0 without existingItem, remove the room
                    return updatedQuantity > 0 ? { ...r, quantity: updatedQuantity } : null;
                  }
                  return r;
                })
                .filter(Boolean);

              return { ...project, selectedRooms: updatedRooms };
            } else {
              if (existingItem) {
                const newRoom = {
                  roomLabel,
                  lineItemId: existingItem.id,
                  quantity: existingItem.quantity - 1 > 0 ? existingItem.quantity - 1 : undefined,
                };

                return {
                  ...project,
                  selectedRooms: [...project.selectedRooms, { ...newRoom }].filter(
                    (r) => r.quantity !== 0 || !r.quantity,
                  ),
                };
              }
            }
          }
          return project;
        });

        return updatedProjects.filter((project) => project.selectedRooms.length > 0);
      }

      // If the project is not found but an existingItem is provided, create a new project with remove room action
      if (existingItem) {
        return [
          ...prevSelectedProjects,
          {
            shoppingListId,
            selectedRooms: [
              {
                roomLabel,
                lineItemId: existingItem.id,
                quantity: existingItem.quantity - 1 > 0 ? existingItem.quantity - 1 : undefined,
              },
            ].filter((r) => r.quantity !== 0 || !r.quantity),
          },
        ];
      }

      return prevSelectedProjects;
    });
  };

  const handleIncrement = (shoppingListId: string, roomLabel: any, existingItem?: any) => {
    const projectData = effectiveFromProjectBuilder ? projectFormList : projectForm;
    const productSku = productToAdd?.sku?.toUpperCase().trim() || '';

    let normalizedRoom = String(roomLabel).trim();
    if (normalizedRoom && !/\d+$/.test(normalizedRoom)) {
      normalizedRoom = `${normalizedRoom} 1`;
    }

    let existingQuantity = 0;
    if (projectData?.lineItems?.length && productSku) {
      projectData.lineItems.forEach((item: any) => {
        if (!item) return;
        const itemSku = (item.variant?.sku || item.sku || '').toUpperCase().trim();
        let itemRoom = String(item.roomName || '').trim();
        if (itemRoom && !/\d+$/.test(itemRoom)) {
          itemRoom = `${itemRoom} 1`;
        }

        if (itemSku === productSku && itemRoom === normalizedRoom) {
          existingQuantity += Number(item.quantity) || 0;
        }
      });
    }
    const currentItemQuantity = existingItem?.quantity || 0;
    const quantityToAdd = 1; // Always adding 1 when incrementing
    const newTotalQuantity = existingQuantity + quantityToAdd;

    if (existingQuantity >= 9999) {
      setError(`No worries — just a quick fix. Quantities need to be 9,999 or less.`);
      setTimeout(() => setError(''), 5000);
      return;
    }

    if (newTotalQuantity > 9999) {
      setError(`No worries — just a quick fix. Quantities need to be 9,999 or less.`);
      setTimeout(() => setError(''), 5000);
      return;
    }

    setSelectedProjects((prevSelectedProjects: any[]) => {
      const projectIndex = prevSelectedProjects.findIndex((project) => project.shoppingListId === shoppingListId);

      if (projectIndex !== -1) {
        const updatedProjects = prevSelectedProjects.map((project) => {
          if (project.shoppingListId === shoppingListId) {
            const roomIndex = project.selectedRooms.findIndex((r: any) => r.roomLabel === roomLabel);

            if (roomIndex !== -1) {
              const updatedRooms = project.selectedRooms.map((r: any) =>
                r.roomLabel === roomLabel ? { ...r, quantity: (r.quantity || 1) + 1 } : r,
              );
              return { ...project, selectedRooms: updatedRooms };
            } else {
              return {
                ...project,
                selectedRooms: [
                  ...project.selectedRooms,
                  {
                    roomLabel,
                    ...(existingItem?.id
                      ? { lineItemId: existingItem.id, quantity: existingItem.quantity + 1 }
                      : { quantity: 1 }),
                  },
                ],
              };
            }
          }
          return project;
        });

        return updatedProjects;
      } else {
        return [
          ...prevSelectedProjects,
          {
            shoppingListId,
            selectedRooms: [
              {
                roomLabel,
                ...(existingItem?.id
                  ? { lineItemId: existingItem?.id, quantity: existingItem.quantity + 1 }
                  : { quantity: 1 }),
              },
            ],
          },
        ];
      }
    });
  };

  const handleSubmit = async () => {
    if (!selectedProjects) return;
    try {
      setLoading(true);
      const promises = selectedProjects.map((p: any) =>
        updateShoppingList({
          shoppingListId: p.shoppingListId,
          sku: productToAdd?.sku ?? '',
          items: p.selectedRooms?.map((r: any) => ({
            ...(r?.lineItemId ? { lineItemId: r.lineItemId } : {}),
            roomName: r.roomLabel,
            ...(r?.quantity ? { quantity: r.quantity } : {}),
            ...(r?.roomNotes !== undefined ? { roomNotes: r.roomNotes } : {}),
          })),
        }),
      );
      const responses = await Promise.all(promises);
      if (responses.length > 0 && responses[0]?.shoppingList?.id) {
        setSuccess(true);
        setTimeout(() => {
          setError('');
          setSuccess(false);
          setSelectedProjects([]);
          handleCancel();
        }, 2000);
        if (effectiveFromProjectBuilder) {
          if (responses[0]?.shoppingList?.lineItems.length) {
            eventEmitter.emit('productAdded', 'Product present');
          } else {
            eventEmitter.emit('productAdded', 'No product present');
          }
        }
        if (typeof window === 'undefined') return;
        (window as any).dataLayer = (window as any).dataLayer || [];
        (window as any).dataLayer.push({
          event: PROJECT_PRODUCTS_UPDATED,
          userId: account?.accountId,
        });
      } else {
        console.info('TODO!');
      }
    } catch (error: any) {
      const errorMessage = (error?.message || error?.toString() || 'Something went wrong!').replace('Error: ', '');

      if (errorMessage == 'Failed to fetch') {
        setError("No internet connection, please try again later");
        return;
      }
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setSelectedProjects([]);
    setError('');
    setVisibleCount(3);
    if (onCancel) {
      onCancel();
    }
  };
  const addRoom = () => {
    setShowCreateRoom(true);
  };
  const cancelRoom = () => {
    setShowCreateRoom(false);
    setNewRoomName('');
    setError('');
  };

  const handleButtonClick = () => {
    router.push(`/${locale}/project-builder/`);
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const projectEditData: any = await getProjectById({
          shoppingListId: projectForm?.shoppingListId,
        });
        setProjectFormList(projectEditData.data);
      } catch (error) {
        console.error('Error fetching project edit data:', error);
      }
      finally {
        setLoadingRooms(false);
      }
    };
    if (projectForm?.shoppingListId) {
      setLoadingRooms(true);
      fetchData();
    }
  }, []);


  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const regex = /[^a-zA-Z0-9\s-]/g;
    const sanitizedValue = e.target.value.replace(regex, '');
    setNewRoomName(sanitizedValue);
  };

  const isDuplicate = (newItem: string, existingArray: string[]): boolean => {
    return existingArray.some(item => (item.toLowerCase()) === newItem.toLowerCase());
  };

  const toTitleCase = (str: string): string => {
    return str
      .toLowerCase()
      .split(' ')
      .filter(word => word.trim() !== '')
      .map(word => word[0].toUpperCase() + word.slice(1))
      .join(' ');
  };

  const handleCreateRoom = async () => {
    const trimmedRoom = newRoomName.trim();
    if (!trimmedRoom) return;

    const checkIsDuplicate = isDuplicate(toTitleCase(trimmedRoom), projectFormList?.roomLabels||[]);
    if (checkIsDuplicate) {
      setError('Room already exists!');
      setTimeout(() => setError(''), 5000);
      return;
    }
    const roomLables = [...(projectFormList?.roomLabels??[]), toTitleCase(trimmedRoom)];
    const updatedProjectForm = {
      ...projectFormList,
      roomLabels: roomLables
    };
    const payload = {
      version: projectForm.version,
      shoppingListId: projectForm.shoppingListId,
      items: {
        selectedRoomDetails: updatedProjectForm?.roomLabels,
      },
    };
    try {
      const responseBody: any = await updateProjectStatus(payload);
      setProjectFormList(updatedProjectForm);
      setNewRoomName('');
      setShowCreateRoom(false);
    } catch (error) {
      console.error('Error on creating room:', error);
    }
  };
  const sortedResults = [...(shoppingLists?.data?.results ?? [])].sort(
  (a, b) =>
    new Date(b.lastModifiedAt).getTime() -
    new Date(a.lastModifiedAt).getTime()
)
  return (
    <>
      <div>
          {!effectiveFromProjectBuilder ? (
            <Popup isOpen={open && !shoppingLists?.loading} bodyClassNames=" mx-[20px] sm:mx-unset  max-w-[535px]">
              <Typography className="px-4 pt-4 md:px-[32px] md:pt-[32px] text-18 font-semibold text-black">
                {`Add ${productToAdd?.name ?? ''} to the following projects`}
              </Typography>
              <div className="max-h-[43vh] overflow-y-auto px-4 md:px-[32px] md:max-h-[54vh] lg:max-h-[68vh]">
                {shoppingLists?.data?.results?.length === 0 && !shoppingLists?.loading && (
                  <div className="pt-[16px] text-14 text-[#666666]">
                    No projects available.{' '}
                    <Link href={`/${locale}/project-builder/create`}>
                      <span className="underline">Click here</span>
                    </Link>{' '}
                    to create a new project.
                  </div>
                )}
                {open &&
                  !shoppingLists?.loading &&
                  !shoppingLists?.loading &&
  sortedResults.slice(0, visibleCount).map((project: any) => {
                    return (
                      <div className="mt-[19px]" key={project.shoppingListId}>
                        <AccordionButton
                          closedSectionTitle={project.projectName}
                          className="border-t border-[#DDDDDD]"
                          buttonClassName="px-[12px] py-[16px] text-16 font-normal text-[#090909] text-left font-semibold"
                          panelClassName="text-14 2xl:text-16 text-[#090909] font-normal"
                          iconClassName="w-[20px] h-[20px] stroke-2 text-[#323C43]"
                        >
                          <Rooms
                            project={project}
                            handleDecrement={handleDecrement}
                            handleIncrement={handleIncrement}
                            selectedProjects={selectedProjects}
                            handleRoomSelection={handleRoomSelection}
                            handleAddNote={handleAddNote}
                            productToAdd={productToAdd}
                          />
                          {showCreateRoomPDPId === project.shoppingListId ? (
                            <div className="mt-[10px] flex flex-col gap-[4px] px-[12px] pb-[16px]" style={{ maxWidth: '318px' }}>
                              <input
                                type="text"
                                className="rounded border px-[4px] py-[2px] w-full"
                                placeholder="Room Name"
                                value={newRoomName}
                                onChange={handleChange}
                                maxLength={30}
                              />
                              <div className="flex gap-[4px]">
                                <Button
                                  className="rounded border cursor-pointer !bg-[#1A1A1A]"
                                  onClick={() => handleCreateRoomPDP(project)}
                                  type="button"
                                  variant="primary"
                                >
                                  <Typography as="span" className={`text-14 font-semibold text-[#fff]`}>
                                    Add Room
                                  </Typography>
                                </Button>
                                <Button
                                  className="rounded border bg-white cursor-pointer"
                                  onClick={cancelRoomPDP}
                                  type="button"
                                  variant="secondary"
                                >
                                  <Typography as="span" className={`text-14 font-semibold text-[#090909]`}>
                                    Cancel
                                  </Typography>
                                </Button>
                              </div>
                            </div>
                          ) : (
                            <div onClick={() => setShowCreateRoomPDPId(project.shoppingListId)} className="mt-[10px] px-[12px] pb-[16px] inline-flex cursor-pointer items-center gap-[2px]">
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                fill="none"
                                viewBox="0 0 24 24"
                                strokeWidth={1.5}
                                stroke="currentColor"
                                className="h-[25px] w-[25px]"
                              >
                                <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
                              </svg>
                              <span className="font-semibold">Add a Room</span>
                            </div>
                          )}
                        </AccordionButton>
                      </div>
                    );
                  })}
              {open &&
                !shoppingLists?.loading &&
                visibleCount < Math.min(sortedResults.length, 10) && (
                  <div className="mt-[20px] mb-[10px] w-full flex justify-center mx-auto text-center">
                    <Button
                      onClick={() =>
                        setVisibleCount(
                          Math.min(sortedResults.length, 10)
                        )
                      }
                      className="border-1 z-10 w-full !bg-[#1A1A1A] cursor-pointer rounded-none border !border-[#2B2C39] bg-white px-[25px] py-[10px] shadow-sm hover:!border-[#F4F4F4]"
                    >
                      <Typography
                        as="span"
                        className="text-14 font-semibold text-white"
                      >
                        Show More Projects
                      </Typography>
                    </Button>
                  </div>
                )}
              </div>
              <div className="px-[32px] pb-[32px]">
                {success && !error && (
                  <Typography className="!mt-[20px] text-14 text-green-600">
                    Product has been successfully added.
                  </Typography>
                )}
                {error && <span className="padding-l-4 text-error-600 text-12 font-semibold">{error}</span>}
              <div className="flex flex-col items-center justify-center gap-[24px] pt-[24px] sm:flex-row sm:gap-[32px]">
                <Button
                  disabled={selectedProjects?.length === 0 || loading}
                  onClick={() => handleSubmit()}
                  className="border-1 z-10 w-full sm:w-[220px] rounded-none border !border-[#2B2C39] !bg-[#1A1A1A] px-[25px] py-[13px] shadow-sm hover:!bg-[#394A54] disabled:!border-neutral-400 disabled:!bg-neutral-400"
                >
                  <Typography as="span" className="text-14 font-semibold text-white">
                    Save and continue
                  </Typography>
                </Button>

                <Button
                  onClick={handleCancel}
                  className="border-1 z-10 w-full sm:w-[220px] rounded-none border !border-[#2B2C39] bg-white px-[25px] py-[13px] shadow-sm hover:!border-[#F4F4F4] hover:!bg-[#F4F4F4]"
                >
                  <Typography as="span" className="text-14 font-semibold text-[#090909]">
                    Cancel
                  </Typography>
                </Button>
              </div>
              </div>
            </Popup>
          ) : (
            <>
              <Popup isOpen={open && !shoppingLists?.loading} bodyClassNames=" mx-[20px] sm:mx-unset  max-w-[535px]">
                <Typography className="px-4 pt-4 md:px-[32px] md:pt-[32px] text-18 font-semibold text-black">
                  {`Add ${productToAdd?.name ?? ''} to the following rooms`}
              </Typography>
              <div className="max-h-[43vh] overflow-y-auto px-[32px] md:max-h-[54vh] lg:max-h-[68vh]">
                {open && !loadingRooms && (
                  <div className="mt-[20px]">
                    <Rooms
                      project={projectFormList}
                      handleDecrement={handleDecrement}
                      handleIncrement={handleIncrement}
                      selectedProjects={selectedProjects}
                      handleRoomSelection={handleRoomSelection}
                      handleAddNote={handleAddNote}
                      productToAdd={productToAdd}
                    />
                    {showCreateRoom && (
                      <div className="mt-[10px] flex flex-col gap-[4px]" style={{ maxWidth: '318px' }}>
                        <input
                          type="text"
                          className="rounded border px-[4px] py-[2px] w-full"
                          placeholder="Room Name"
                          value={newRoomName}
                          onChange={handleChange}
                          maxLength={30}
                        />
                        <div className="flex gap-[4px]">
                          <Button
                            className="rounded border cursor-pointer !bg-[#1A1A1A]"
                            onClick={handleCreateRoom}
                            type="button"
                            variant='primary'
                          >

                            <Typography as="span" className={`text-14 font-semibold text-[#fff]`}>
                              Add Room
                            </Typography>
                          </Button>
                          <Button
                            className="rounded border bg-white cursor-pointer"
                            onClick={cancelRoom}
                            type="button"
                            variant='secondary'
                          >
                            <Typography as="span" className={`text-14 font-semibold text-[#090909]`}>
                              Cancel
                            </Typography>
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                )}
                {loadingRooms && <div className='w-full h-[150px] flex items-center justify-center'><Loader /></div>}
                {!loadingRooms && <div onClick={addRoom} className="mt-[10px] inline-flex cursor-pointer items-center gap-[2px]">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth={1.5}
                    stroke="currentColor"
                    className="h-[25px] w-[25px]"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
                  </svg>
                  <span className="font-semibold">Add a Room</span>
                  </div> }

              </div>
              <div className="px-[32px] pb-[32px]">
                {success && !error && (
                  <Typography className="!mt-[20px] text-14 text-green-600">
                    Product has been successfully added.
                  </Typography>
                )}
                {error && <span className="padding-l-4 text-error-600 text-12 font-semibold">{error}</span>}
                <div className="flex flex-col items-center gap-[24px] pt-[24px] sm:flex-row sm:gap-[32px] ">
                  <Button
                    disabled={selectedProjects?.length === 0 || loading}
                    onClick={() => handleSubmit()}
                    className={
                      'border-1 cursor-pointer z-10 w-full rounded-none border !border-[#2B2C39] !bg-[#1A1A1A] px-[25px] py-[13px] shadow-sm hover:!bg-[#394A54] disabled:!border-neutral-400 disabled:!bg-neutral-400 md:w-fit'
                    }
                  >
                    <Typography as="span" className={`text-14 font-semibold text-[#fff]`}>
                      Save and continue
                    </Typography>
                  </Button>
                  <Button
                    onClick={handleCancel}
                    className={
                      'border-1 z-10 w-full cursor-pointer rounded-none border !border-[#2B2C39] bg-white px-[25px] py-[13px] shadow-sm hover:!border-[#F4F4F4] hover:!bg-[#F4F4F4] md:w-fit'
                    }
                  >
                    <Typography as="span" className={`text-14 font-semibold text-[#090909]`}>
                      Cancel
                    </Typography>
                  </Button>
                  <Button
                    onClick={handleButtonClick}
                    className={
                      'border-1 z-10 w-full cursor-pointer rounded-none border !border-[#2B2C39] bg-white px-[25px] py-[13px] shadow-sm hover:!border-[#F4F4F4] hover:!bg-[#F4F4F4] md:w-fit'
                    }
                  >
                    <Typography as="span" className={`text-14 font-semibold text-[#090909]`}>
                      View projects
                    </Typography>
                  </Button>
                </div>
              </div>
            </Popup>
          </>
        )}
      </div>
      {(loading || (shoppingLists?.loading && productToAdd)) &&<> <Spinner />
        <div className="overlay fixed left-0 top-0 z-50 h-full w-full bg-gray-800 opacity-75"></div>
      </>}
    </>
  );
};

export default AddProductToProject;
